import { ActionArgs } from 'xstate';
import { ActorErrorParams } from '../../../../lib/types';
import { logger } from '../../../../lib/logger';

import {
  CreatePaymentMethodResult,
  FetchPaymentMethodResult,
  FetchPaymentMethodsResult,
} from './types/actors';
import { PaymentMachineEvents } from './types/events';
import { PaymentsContext } from './paymentsMachine';
import { getPaymentMethodError } from './utils';

type PaymentsActionArgs = ActionArgs<
  PaymentsContext,
  PaymentMachineEvents,
  PaymentMachineEvents
>;

/**
 * Handles completion of fetch payment method actor
 * @param _ PaymentsActionArgs (context, event, self)
 * @param __ FetchPaymentMethodResult
 * @returns void
 */
export const handleFetchPaymentMethodDone = (
  _: PaymentsActionArgs,
  params: FetchPaymentMethodResult
): void => {
  params.resolve(params.paymentMethodRes);
};

/**
 * Handles completion of fetch payment methods actor
 * @param _ PaymentsActionArgs (context, event, self)
 * @param __ FetchPaymentMethodsResult
 * @returns void
 */
export const handleFetchPaymentMethodsDone = (
  _: PaymentsActionArgs,
  params: FetchPaymentMethodsResult
): void => {
  params.resolve(params.paymentMethodsRes);
};

/**
 * Handles completion of create payment method actor
 * @param _  PaymentsActionArgs (context, event, self)
 * @param params CreatePaymentMethodResult
 */
export const handleCreatePaymentMethodDone = (
  _: PaymentsActionArgs,
  params: CreatePaymentMethodResult
): void => {
  params.resolve(params.createPaymentMethodsRes);
};

type LogErrorDeps = {
  merchantID?: string | null;
};

/**
 * Factory for `logError`. Captures the merchant ID for structured log
 * enrichment.
 * @param deps
 * @returns action that logs an error with application and merchant context
 */
export const createLogError =
  (deps: LogErrorDeps) =>
  (_: PaymentsActionArgs, params: ActorErrorParams): void => {
    logger.error(
      {
        err: params.error,
        merchantID: deps?.merchantID,
        buyerID: params.buyerID,
      },
      `Error in PaymentsMachine: ${params.type}`
    );
  };

/**
 * Factory for `logEvaluatedErrorResponse`. Captures the merchant ID for structured log
 * @param deps LogErrorDeps
 * @returns action that logs an evaluated error response with application and merchant context
 */
export const createLogEvaluatedErrorResponse =
  (deps: LogErrorDeps) =>
  (_: PaymentsActionArgs, params: unknown): void => {
    const paymentMethodError = getPaymentMethodError(
      params as
        | FetchPaymentMethodResult
        | CreatePaymentMethodResult
        | FetchPaymentMethodsResult
    );
    logger.error(
      {
        err: paymentMethodError,
        merchantID: deps?.merchantID,
      },
      'Error in PaymentsMachine'
    );
  };

/**
 * Handles service errors by rejecting the associated promise
 * @param _ PaymentsActionArgs
 * @param params ActorErrorParams
 */
export const handleServiceError = (
  _: PaymentsActionArgs,
  params: ActorErrorParams
): void => {
  params.reject(params.error);
};
